import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', KES: 'KSh', NGN: '₦', ZAR: 'R',
  GHS: 'GH₵', TZS: 'TSh', UGX: 'USh', INR: '₹',
};

export default function JoinPage() {
  const [inviteCode] = useState(() => window.location.pathname.split('/join/')[1] || '');
  const [groupInfo, setGroupInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });

  useEffect(() => {
    if (!inviteCode) { setError('No invite code found'); setLoading(false); return; }
    axios.get(`${API}/invite/${inviteCode}`)
      .then(res => { setGroupInfo(res.data); setLoading(false); })
      .catch(err => {
        setError(err.response?.data?.detail || 'Invalid or expired invite link');
        setLoading(false);
      });
  }, [inviteCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error('Name and phone number are required'); return; }
    if (form.password && !form.email) { toast.error('Email is required to create a login account'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/invite/${inviteCode}/join`, form);
      setSuccess(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join');
    } finally {
      setSubmitting(false);
    }
  };

  const sym = CURRENCY_SYMBOLS[groupInfo?.currency] || groupInfo?.currency || '$';

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#F1F5F9' }}>
      <div style={{ textAlign: 'center', color: '#64748B' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px', animation: 'spin 1s linear infinite' }}>Loading...</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#F1F5F9' }}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '40px', maxWidth: '440px', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>!</div>
        <h2 style={{ color: '#DC2626', margin: '0 0 8px' }}>Link Not Valid</h2>
        <p style={{ color: '#64748B' }}>{error}</p>
        <a href="/login" style={{ display: 'inline-block', marginTop: '20px', padding: '10px 24px', background: '#3B82F6', color: '#fff', borderRadius: '8px', textDecoration: 'none' }}>
          Go to Login
        </a>
      </div>
    </div>
  );

  if (success) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#F1F5F9' }}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '40px', maxWidth: '500px', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '28px' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3"><path d="M5 13l4 4L19 7" /></svg>
        </div>
        <h2 style={{ color: '#1E293B', margin: '0 0 8px' }}>Welcome to {success.group_name}!</h2>
        {success.membership_number && (
          <p style={{ color: '#10B981', fontWeight: 600, fontSize: '18px', margin: '8px 0' }}>
            Membership #: {success.membership_number}
          </p>
        )}
        <p style={{ color: '#64748B', marginBottom: '24px' }}>
          {success.account_created 
            ? 'Your account has been created. You can now log in.' 
            : 'You have been added to the group. Ask your group leader for login access.'}
        </p>
        <a href="/login" data-testid="go-to-login-btn" style={{ display: 'inline-block', padding: '12px 32px', background: '#3B82F6', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 600 }}>
          Go to Login
        </a>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#F1F5F9', padding: '20px' }}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '40px', maxWidth: '500px', width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <h1 style={{ color: '#1E293B', fontSize: '24px', margin: '0 0 4px' }}>Join {groupInfo.group_name}</h1>
          <p style={{ color: '#64748B', margin: 0, fontSize: '14px' }}>{groupInfo.group_description}</p>
        </div>
        
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <div style={{ background: '#F0F9FF', padding: '10px 16px', borderRadius: '8px', fontSize: '13px', color: '#0369A1' }}>
            <strong>{groupInfo.member_count}</strong> members
          </div>
          <div style={{ background: '#F0FFF4', padding: '10px 16px', borderRadius: '8px', fontSize: '13px', color: '#15803D' }}>
            {sym}{groupInfo.payment_amount} / {groupInfo.payment_frequency}
          </div>
          <div style={{ background: '#FEF3C7', padding: '10px 16px', borderRadius: '8px', fontSize: '13px', color: '#92400E' }}>
            {groupInfo.group_category?.replace('_', ' ')}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '6px' }}>Full Name *</label>
            <input
              type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
              data-testid="join-name-input"
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              placeholder="Your full name"
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '6px' }}>Phone Number *</label>
            <input
              type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required
              data-testid="join-phone-input"
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              placeholder="+254..."
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '6px' }}>Email {form.password ? '*' : '(optional)'}</label>
            <input
              type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              data-testid="join-email-input"
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              placeholder="your.email@example.com"
            />
            <small style={{ color: '#9CA3AF', fontSize: '12px' }}>Required only if you want to create a login account</small>
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '6px' }}>Password (optional)</label>
            <input
              type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
              data-testid="join-password-input"
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              placeholder="Create a password to log in"
            />
            <small style={{ color: '#9CA3AF', fontSize: '12px' }}>Set a password to create your login account (requires email)</small>
          </div>
          <button
            type="submit" disabled={submitting}
            data-testid="join-submit-btn"
            style={{
              width: '100%', padding: '12px', background: '#10B981', color: '#fff', border: 'none', borderRadius: '8px',
              fontWeight: 600, fontSize: '16px', cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.7 : 1
            }}
          >
            {submitting ? 'Joining...' : `Join ${groupInfo.group_name}`}
          </button>
        </form>
        
        <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', color: '#9CA3AF' }}>
          Already a member? <a href="/login" style={{ color: '#3B82F6' }}>Log in</a>
        </p>
      </div>
    </div>
  );
}
